import React, {useRef} from 'react';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import './ArrBrandingLocation.scss';

export type BrandingPosition = 'Left' | 'Center' | 'Right';

export interface ArrBrandingLocationProps {
  label?: string;
  placeHolderName?: string;
  className?: string;
  activeKey: BrandingKey | null;
  filledKeys: BrandingKey[];
  disabledKeys?: BrandingKey[];
  onPositionChange: (key: BrandingKey) => void;
  onReset: () => void;
  dataTestId: string;
}

type BrandingKey =
  | 'top-Left'
  | 'top-Center'
  | 'top-Right'
  | 'bottom-Left'
  | 'bottom-Center'
  | 'bottom-Right';

const positions: Array<BrandingPosition> = ['Left', 'Center', 'Right'];

const ArrBrandingLocation: React.FC<ArrBrandingLocationProps> = ({
  label = 'Location',
  placeHolderName = 'Select a location',
  className,
  activeKey,
  filledKeys = [],
  onPositionChange,
  onReset,
  disabledKeys = [],
  dataTestId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resetBtnRef = useRef<HTMLButtonElement | null>(null);
  const isEntryResetFocusRef = useRef(false);

  const getButtonVariant = (key: BrandingKey) => {
    if (activeKey === key) return 'primary';
    if (filledKeys.includes(key)) return 'success';
    return 'secondary';
  };

  const focusTopLeftButton = () => {
    if (!containerRef.current) return;
    const leftButtons = containerRef.current.querySelectorAll(
      '[data-testid="arr-branding-location-button-Left"]'
    );
    const topLeftButton = leftButtons[0] as HTMLElement | undefined;
    topLeftButton?.focus();
  };

  const focusBottomLeftButton = () => {
    if (!containerRef.current) return;
    const leftButtons = containerRef.current.querySelectorAll(
      '[data-testid="arr-branding-location-button-Left"]'
    );
    const bottomLeftButton = leftButtons[1] as HTMLElement | undefined;
    bottomLeftButton?.focus();
  };

  const focusTopRightButton = () => {
    if (!containerRef.current) return;
    const rightButtons = containerRef.current.querySelectorAll(
      '[data-testid="arr-branding-location-button-Right"]'
    );
    const topRightButton = rightButtons[0] as HTMLElement | undefined;
    topRightButton?.focus();
  };

  const handleContainerFocusCapture = (e: React.FocusEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const cameFromOutside = !container.contains(e.relatedTarget as Node | null);
    const leftButtons = container.querySelectorAll(
      '[data-testid="arr-branding-location-button-Left"]'
    );
    const topLeftButton = leftButtons[0];
    if (cameFromOutside && e.target === topLeftButton) {
      isEntryResetFocusRef.current = true;
      requestAnimationFrame(() => {
        resetBtnRef.current?.focus();
      });
    }
  };

  const handleResetKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      if (isEntryResetFocusRef.current) {
        isEntryResetFocusRef.current = false;
        focusTopLeftButton();
      } else {
        focusBottomLeftButton();
      }
    }
  };

  const handlePositionKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    key: BrandingKey
  ) => {
    if (e.key === 'Tab' && !e.shiftKey && key === 'top-Right') {
      e.preventDefault();
      isEntryResetFocusRef.current = false;
      focusBottomLeftButton();
      return;
    }
    if (e.key === 'Tab' && e.shiftKey && key === 'bottom-Left') {
      e.preventDefault();
      focusTopRightButton();
      return;
    }
    if (e.key === 'Tab' && e.shiftKey && key === 'top-Left') {
      e.preventDefault();
      isEntryResetFocusRef.current = false;
      resetBtnRef.current?.focus();
    }
  };

  const renderPositionButtons = (row: 'top' | 'bottom') =>
    positions.map((pos, index) => {
      const key = `${row}-${pos}` as BrandingKey;

      const isDisabled = disabledKeys?.includes(key);
      const ariaLabel = `${row} ${pos}`;

      return (
        <React.Fragment key={key}>
          <ArrButton
            size="small"
            variant={getButtonVariant(key)}
            disabled={isDisabled}
            aria-label={ariaLabel}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
              handlePositionKeyDown(e, key)
            }
            onClick={() => !isDisabled && onPositionChange(key)}
            dataTestId={`arr-branding-location-button-${pos}`}
          >
            {pos}
          </ArrButton>
          {index < positions.length - 1 && (
            <span
              className="arr-branding-location__inline-divider"
              data-testid={`arr-branding-location-span-${index}-${dataTestId}`}
            />
          )}
        </React.Fragment>
      );
    });

  return (
    <Box
      className={`${className} arr-branding-location`}
      data-testid={dataTestId}
      ref={containerRef}
      onFocusCapture={handleContainerFocusCapture}
    >
      <Box className="arr-branding-location__row">
        {renderPositionButtons('top')}
      </Box>
      {/* Center dashed container */}
      <Box className="arr-branding-location__dropzone">
        <Typography
          className="typo-text label3_size label"
          data-testid={`arr-branding-location-label-${dataTestId}`}
        >
          {label}
        </Typography>

        <Typography
          className="typo-text subhead1_size placeholder"
          data-testid={`arr-branding-location-key-${dataTestId}`}
        >
          {activeKey
            ? activeKey
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join('-')
            : placeHolderName}
        </Typography>

        <ArrButton
          size="small"
          variant="secondary"
          onClick={onReset}
          onKeyDown={handleResetKeyDown}
          className="reset-button"
          dataTestId="arr-branding-location-reset-button"
          ref={resetBtnRef}
        >
          Reset
        </ArrButton>
      </Box>
      {/* Bottom controls */}
      <Box className="arr-branding-location__row">
        {renderPositionButtons('bottom')}
      </Box>
    </Box>
  );
};

export default ArrBrandingLocation;
